import React, { ChangeEvent, useRef, useState } from 'react';
import { IFeedback } from '../shared/models/feedback';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { sendFeedback } from '../shared/api/feedback-api';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import { useIntl } from 'react-intl';
import translate from '../i18n/translate';

import usersGuide from '../assets/documents/users_guide.pdf';
import installationInstructions from '../assets/documents/installation_instructions.pdf';
import softwareAdditionalInteraction from '../assets/documents/software_additional_interaction.pdf';
import lifeCycleProcessesDescription from '../assets/documents/life_cycle_processes_description.pdf';

const Form = () => {
    const isBot = useRef(false);
    const [isLoading, setLoading] = useState(false);
    const [hasConsent, setHasConsent] = useState(false);
    const intl = useIntl();
    const en = intl.locale === 'en';
    const requiredMessage = en ? 'This field is required' : 'Обязательное поле для заполнения';
    const [status, setStatus] = useState('');
    const schema = yup.object().shape({
        email: yup.string()
            .required(requiredMessage)
            .email(en ? 'Enter a valid email address' : 'Введите корректный адрес электронной почты'),
        name: yup.string()
            .required(requiredMessage)
            .min(2, en ? 'Enter at least 2 characters' : 'Минимальная длина - 2 символа')
            .max(256, en ? 'Too many characters' : 'Превышено максимальное количество символов'),
        phoneNumber: yup.string().required(requiredMessage)
            .min(4, en ? 'Enter at least 4 characters' : 'Минимальная длина - 4 символа')
            .max(30, en ? 'Too many characters' : 'Превышено максимальное количество символов'),
        message: yup.string()
            .required(requiredMessage)
            .min(2, en ? 'Enter at least 2 characters' : 'Минимальная длина - 2 символа'),
    });

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<IFeedback>({
        resolver: yupResolver(schema),
    });

    const onSubmit = (feedback: IFeedback) => {
        if (isBot.current || !hasConsent) {
            return;
        }
        setStatus(en ? 'Sending your message…' : 'Отправка обращения…');
        setLoading(true);
        sendFeedback(feedback)
            .then(
                (data) => {
                    if (data.data.status === 'SUCCESS') {
                        setStatus(en ? 'Your message was sent successfully.' : 'Ваше обращение успешно отправлено.');
                        reset();
                        setHasConsent(false);
                        toast.success(<div>Ваше обращение успешно отправлено.<br/>
                                           В ближайшее время мы с вами свяжемся!</div>);
                    } else {
                        setStatus(en ? 'Could not send your message. Please try again.' : 'Не удалось отправить обращение. Попробуйте снова.');
                    }
                },
                (error) => {
                    setStatus(en ? 'Could not send your message. Please try again.' : 'Не удалось отправить обращение. Попробуйте снова.');
                    console.log(error);
                    toast.error(<div>Извините, что-то пошло не так.<br/>
                                     Попробуйте снова, либо обратитесь позже</div>);
                },
            )
            .finally(() => setLoading(false));
    };

    const catchBot = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.value) {
            isBot.current = true;
        }
    };

    return (
        <section className='form'>
            <div className='container'>
                <div className='cover_outside' style={{ gap: '2rem' }}>
                    <div className='form_data'>
                        <div>
                            <div className='doc-title'>{translate('documentation')}</div>
                            <div className='doc-list'>
                                <div className='doc-link'>
                                    <a href={usersGuide} target='_blank' rel='noreferrer'>{translate('usersGuide')}</a>
                                </div>
                                <div className='doc-link'>
                                    <a href={installationInstructions} target='_blank' rel='noreferrer'>{translate('installationInstructions')}</a>
                                </div>
                                <div className='doc-link'>
                                    <a href={softwareAdditionalInteraction} target='_blank' rel='noreferrer'>{translate('softwareAdditionalInteraction')}</a>
                                </div>
                                <div className='doc-link'>
                                    <a href={lifeCycleProcessesDescription} target='_blank' rel='noreferrer'>{translate('lifeCycleProcessesDescription')}</a>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className='support-title'>{translate('purchaseService')}</div>
                            <div className='support-list'>
                                <div className='support__link'>
                                    <span className='support__link__label'>{translate('supportPhoneNumber')}:</span>
                                    <a className='support__link__content' href='tel:+74952151671'>+7(495)215-16-71</a>
                                </div>
                                <div className='support-link'>
                                    <span className='support__link__label'>Email:</span>
                                    <a className='support__link__content' href='mailto:info@axiomatika.ru'>info@axiomatika.ru</a>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className='support-title'>{translate('supportService')}</div>
                            <div className='support-list'>
                                <div className='support__link'>
                                    <span className='support__link__label'>{translate('supportPhoneNumber')}:</span>
                                    <a className='support__link__content' href='tel:+74953748864'>+7(495)374-88-64</a>
                                </div>
                                <div className='support-link'>
                                    <span className='support__link__label'>Email:</span>
                                    <a className='support__link__content' href='mailto:support@axiomatika.ru'>support@axiomatika.ru</a>
                                </div>
                            </div>
                        </div>
                        <br/>
                        <a href='https://axiomatika.ru/ocenka-tryda.pdf' target='_blank' rel='noreferrer'>{translate('fLaborEvaluation')}</a>

                    </div>
                    <div className='form_block'>
                        <form onSubmit={handleSubmit(onSubmit)} aria-busy={isLoading}>
                            <ul>
                                <li>
                                    <label htmlFor='feedback-name'>{translate('fullName')}</label>
                                    <input id='feedback-name' autoComplete='name' aria-invalid={!!errors.name} aria-describedby={errors.name ? 'feedback-name-error' : undefined}  type='text' placeholder={en ? 'Your name' : 'Ваше Имя'} disabled={isLoading}
                                        {...register('name')}
                                        required/>
                                    <span id='feedback-name-error' className='form__text form__text_error-message' aria-live='polite'>{errors.name?.message}</span>
                                </li>
                                <li>
                                    <label htmlFor='feedback-email'>{translate('email')}</label>
                                    <input id='feedback-email' autoComplete='email' aria-invalid={!!errors.email} aria-describedby={errors.email ? 'feedback-email-error' : undefined} type='email' placeholder='mail@example.com' disabled={isLoading}
                                        {...register('email')}
                                        required/>
                                    <span id='feedback-email-error' className='form__text form__text_error-message' aria-live='polite'>{errors.email?.message}</span>
                                </li>
                                <li>
                                    <label htmlFor='feedback-phoneNumber'>{translate('phoneNumber')}</label>
                                    <input id='feedback-phoneNumber' autoComplete='tel' aria-invalid={!!errors.phoneNumber} aria-describedby={errors.phoneNumber ? 'feedback-phoneNumber-error' : undefined} type='tel' disabled={isLoading}
                                        {...register('phoneNumber')}
                                        required/>
                                    <span id='feedback-phoneNumber-error' className='form__text form__text_error-message' aria-live='polite'>{errors.phoneNumber?.message}</span>
                                </li>
                                <li>
                                    <label htmlFor='feedback-message'>{translate('message')}</label>
                                    <textarea id='feedback-message' autoComplete='off' aria-invalid={!!errors.message} aria-describedby={errors.message ? 'feedback-message-error' : undefined} disabled={isLoading}
                                        {...register('message')}
                                        required/>
                                    <span id='feedback-message-error' className='form__text form__text_error-message' aria-live='polite'>{errors.message?.message}</span>
                                </li>
                                <li>
                                    <div className='form__consent'>
                                        <input id='feedback-consent' type='checkbox' checked={hasConsent}
                                            onChange={(e) => setHasConsent(e.target.checked)} disabled={isLoading} required/>
                                        <label htmlFor='feedback-consent'>
                                            {translate('fSubmitButtonAgree')}{' '}
                                            <a href='https://axiomatika.ru/person-dannye.php' target='_blank' rel='noreferrer'>
                                                {translate('fPersonalData')}
                                            </a>{' '}
                                            {translate('fConsentAnd')}{' '}
                                            <a href='https://axiomatika.ru/politika-inform-bez.pdf' target='_blank' rel='noreferrer'>
                                                {translate('fPrivacyPolicy')}
                                            </a>
                                        </label>
                                    </div>
                                </li>
                                <li>
                                    <input type='submit' value={intl.formatMessage({ id: 'send' })}
                                        disabled={isLoading || !hasConsent}/>
                                </li>
                                <li hidden>
                                    <input type='text' aria-hidden='true' tabIndex={-1} autoComplete='off' name='theme' className='form-theme'
                                        placeholder='theme' onChange={(e) => catchBot(e)}/>
                                </li>
                            </ul>
                            <p role='status' aria-live='polite' aria-atomic='true'>{status}</p>
                        </form>
                    </div>
                </div>
                <br/>
            </div>
        </section>
    );
};

export default Form;
